import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  ErrorOutline as ErrorOutlineIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { BRAND } from './login/brand';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export const REQUIRED_MONTHS = 3;
export const MAX_MONTHS = 12;
const SELECTABLE_MONTHS = 24;
// The API runs as a Vercel serverless function, which rejects request bodies over 4.5 MB.
const MAX_FILE_BYTES = 4.5 * 1024 * 1024;
const ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

const DOC_TYPE = 'business_bank_statement';

const MONTH_WORDS = {
  jan: 1, january: 1, ene: 1, enero: 1,
  feb: 2, february: 2, febrero: 2,
  mar: 3, march: 3, marzo: 3,
  apr: 4, april: 4, abr: 4, abril: 4,
  may: 5, mayo: 5,
  jun: 6, june: 6, junio: 6,
  jul: 7, july: 7, julio: 7,
  aug: 8, august: 8, ago: 8, agosto: 8,
  sep: 9, sept: 9, september: 9, septiembre: 9, setiembre: 9,
  oct: 10, october: 10, octubre: 10,
  nov: 11, november: 11, noviembre: 11,
  dec: 12, december: 12, dic: 12, diciembre: 12,
};

const monthKey = (month, year) => `${year}-${String(month).padStart(2, '0')}`;

/** Most recent statement months, newest first, starting with last month. */
export const recentStatementMonths = (count, now = new Date()) => {
  const out = [];
  for (let i = 1; i <= count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ month: d.getMonth() + 1, year: d.getFullYear(), key: monthKey(d.getMonth() + 1, d.getFullYear()) });
  }
  return out;
};

const isPlausible = (month, year, now) => {
  if (!month || !year || month < 1 || month > 12) return false;
  if (year < now.getFullYear() - 5) return false;
  const current = now.getFullYear() * 12 + now.getMonth();
  return year * 12 + (month - 1) <= current;
};

/**
 * Best-effort guess of the statement month from a file name, e.g.
 * "Statement_Sep_2026.pdf", "eStmt_2026-09-30.pdf", "20260930-statements.pdf",
 * "09-2026 checking.pdf", "Septiembre 2026.pdf".
 */
export const detectStatementMonth = (fileName, now = new Date()) => {
  const base = String(fileName || '').replace(/\.[^.]+$/, '').toLowerCase();
  const result = (month, year) => (isPlausible(month, year, now) ? { month, year } : null);

  // Lookbehind is avoided on purpose: older iOS Safari can't parse it.
  const yearMatch = base.match(/(?:^|\D)(20\d{2})(?!\d)/);
  const words = base.split(/[^a-záéíóúñ]+/).filter(Boolean);
  const word = words.find((w) => MONTH_WORDS[w]);
  const wordMonth = word ? MONTH_WORDS[word] : null;
  if (wordMonth) {
    if (yearMatch) return result(wordMonth, Number(yearMatch[1]));
    const shortYear = base.match(new RegExp(`${word}[\\s_.'-]*(\\d{2})(?!\\d)`));
    if (shortYear) {
      const found = result(wordMonth, 2000 + Number(shortYear[1]));
      if (found) return found;
    }
    // No year in the name: assume the most recent occurrence of that month.
    const year = wordMonth <= now.getMonth() ? now.getFullYear() : now.getFullYear() - 1;
    return result(wordMonth, year);
  }

  const iso = base.match(/(?:^|\D)(20\d{2})[-_.\s]?(0[1-9]|1[0-2])(?:[-_.\s]?(?:0[1-9]|[12]\d|3[01]))?(?!\d)/);
  if (iso) return result(Number(iso[2]), Number(iso[1]));

  const usDate = base.match(/(?:^|\D)(0?[1-9]|1[0-2])[-_.](?:0?[1-9]|[12]\d|3[01])[-_.](20\d{2})(?!\d)/);
  if (usDate) return result(Number(usDate[1]), Number(usDate[2]));

  const monthYear = base.match(/(?:^|\D)(0?[1-9]|1[0-2])[-_.\s](20\d{2})(?!\d)/);
  if (monthYear) return result(Number(monthYear[1]), Number(monthYear[2]));

  return null;
};

const formatSize = (bytes) => (bytes >= 1024 * 1024
  ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
  : `${Math.max(1, Math.round(bytes / 1024))} KB`);

let nextId = 0;

/**
 * Multi-file business bank statement uploader with month auto-detection and a
 * 12-month coverage view. Pass `documents` to control the data from a parent;
 * otherwise the component loads the user's documents itself.
 *
 * Ref API: `open()` opens the file picker (must be called from a user click handler).
 */
const BankStatementUploader = forwardRef(({ documents, onUploaded }, ref) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const fileInputRef = useRef(null);
  const cardRef = useRef(null);
  const [ownDocs, setOwnDocs] = useState([]);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const controlled = Array.isArray(documents);
  const allDocs = controlled ? documents : ownDocs;

  const fetchDocs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOwnDocs(res.data.data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  }, []);

  useEffect(() => {
    if (!controlled) fetchDocs();
  }, [controlled, fetchDocs]);

  const monthName = useCallback((month, year, style = 'long') => (
    new Intl.DateTimeFormat(lang, { month: style, ...(year ? { year: 'numeric' } : {}) })
      .format(new Date(year || 2000, month - 1, 1))
  ), [lang]);

  const statementDocs = useMemo(() => allDocs.filter((d) => d.documentType === DOC_TYPE), [allDocs]);

  const coverage = useMemo(() => {
    const map = {};
    statementDocs.forEach((d) => {
      if (d.statementMonth && d.statementYear) {
        const key = monthKey(Number(d.statementMonth), Number(d.statementYear));
        map[key] = (map[key] || 0) + 1;
      }
    });
    return map;
  }, [statementDocs]);

  const undatedCount = statementDocs.filter((d) => !d.statementMonth || !d.statementYear).length;
  const window12 = useMemo(() => recentStatementMonths(MAX_MONTHS), []);
  const selectable = useMemo(() => recentStatementMonths(SELECTABLE_MONTHS), []);
  const requiredDone = window12.slice(0, REQUIRED_MONTHS).filter((m) => coverage[m.key]).length;
  const monthsOnFile = window12.filter((m) => coverage[m.key]).length;

  const openPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useImperativeHandle(ref, () => ({ open: openPicker }));

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const now = new Date();

    setItems((prev) => {
      const taken = new Set([...Object.keys(coverage), ...prev.map((i) => i.monthKey).filter(Boolean)]);
      const added = files.map((file) => {
        let error = null;
        if (!ALLOWED_TYPES.includes(file.type) && !/\.(pdf|jpe?g|png)$/i.test(file.name)) {
          error = t('bankUpload.badType', 'Only PDF, JPG or PNG files are accepted.');
        } else if (file.size > MAX_FILE_BYTES) {
          error = t('bankUpload.tooLarge', 'This file is over 4.5 MB. Please save a smaller PDF or email it to your advisor.');
        }
        const detected = detectStatementMonth(file.name, now);
        const key = detected ? monthKey(detected.month, detected.year) : '';
        if (key) taken.add(key);
        return { id: ++nextId, file, monthKey: key, detected: !!detected, status: error ? 'error' : 'pending', error, progress: 0 };
      });

      // Fill in files with no detectable month using the most recent months still missing.
      const free = selectable.map((m) => m.key).filter((k) => !taken.has(k));
      added.forEach((item) => {
        if (!item.monthKey && item.status !== 'error') item.monthKey = free.shift() || '';
      });

      const merged = [...prev, ...added];
      return merged.sort((a, b) => (b.monthKey || '').localeCompare(a.monthKey || ''));
    });
  };

  const updateItem = (id, patch) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const blocked = items.some((i) => i.status === 'pending' && !i.monthKey);

  const uploadAll = async () => {
    const queue = items.filter((i) => i.monthKey && (i.status === 'pending' || (i.status === 'error' && i.attempted)));
    if (!queue.length) return;
    setUploading(true);
    const token = localStorage.getItem('token');
    let ok = 0;

    for (const item of queue) {
      updateItem(item.id, { status: 'uploading', progress: 0, error: null });
      const [year, month] = item.monthKey.split('-').map(Number);
      const formData = new FormData();
      formData.append('document', item.file);
      formData.append('documentType', DOC_TYPE);
      formData.append('description', `${DOC_TYPE} ${item.monthKey}`);
      formData.append('statementMonth', String(month));
      formData.append('statementYear', String(year));
      try {
        await axios.post(`${API_URL}/api/documents/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) updateItem(item.id, { progress: Math.round((e.loaded / e.total) * 100) });
          },
        });
        ok++;
        updateItem(item.id, { status: 'done', progress: 100 });
      } catch (err) {
        const status = err.response?.status;
        let message = err.response?.data?.message || t('bankUpload.failed', 'Upload failed. Please try again.');
        if (status === 413) message = t('bankUpload.tooLarge', 'This file is over 4.5 MB. Please save a smaller PDF or email it to your advisor.');
        if (status === 429) message = t('bankUpload.rateLimited', 'Too many uploads at once. Please wait a few minutes and try again.');
        updateItem(item.id, { status: 'error', error: message, attempted: true, progress: 0 });
      }
    }

    setUploading(false);
    if (ok > 0) {
      toast.success(t('bankUpload.uploadedCount', { count: ok, defaultValue: '{{count}} statement(s) uploaded' }));
      setItems((prev) => prev.filter((i) => i.status !== 'done'));
      if (!controlled) fetchDocs();
      onUploaded?.();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!uploading) addFiles(e.dataTransfer.files);
  };

  const pendingCount = items.filter((i) => i.monthKey && (i.status === 'pending' || (i.status === 'error' && i.attempted))).length;

  return (
    <Card
      ref={cardRef}
      id="bank-statements"
      sx={{ border: `1px solid ${BRAND.border}`, borderTop: `4px solid ${BRAND.copper}`, scrollMarginTop: 80 }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => addFiles(e.target.files)}
        />

        {/* Header */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
            bgcolor: 'rgba(232,189,118,0.25)', color: BRAND.copperDark,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AccountBalanceIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {t('bankUpload.title', 'Upload your business bank statements')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('bankUpload.subtitle', 'Your 3 most recent months are required. Add up to 12 months for stronger offers.')}
            </Typography>
          </Box>
        </Stack>

        {/* Coverage */}
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {requiredDone >= REQUIRED_MONTHS
              ? t('bankUpload.requiredDone', 'Required months complete')
              : t('bankUpload.requiredProgress', { done: requiredDone, total: REQUIRED_MONTHS, defaultValue: '{{done}} of {{total}} required months uploaded' })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('bankUpload.monthsOnFile', { count: monthsOnFile, total: MAX_MONTHS, defaultValue: '{{count}} of {{total}} months on file' })}
          </Typography>
        </Stack>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)', md: 'repeat(12, 1fr)' },
          gap: 0.75,
          mb: 1,
        }}>
          {window12.map((m, idx) => {
            const count = coverage[m.key] || 0;
            const required = idx < REQUIRED_MONTHS;
            const queued = items.some((i) => i.monthKey === m.key && i.status !== 'error');
            return (
              <Tooltip
                key={m.key}
                arrow
                title={`${monthName(m.month, m.year)}${required ? ` · ${t('bankUpload.required', 'Required')}` : ''}${count > 1 ? ` · ${t('bankUpload.filesCount', { count, defaultValue: '{{count}} files' })}` : ''}`}
              >
                <Box sx={{
                  textAlign: 'center',
                  py: 0.75,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: count ? 'success.main' : required ? BRAND.copper : BRAND.border,
                  borderStyle: !count && queued ? 'dashed' : 'solid',
                  bgcolor: count ? 'rgba(26,127,90,0.08)' : required ? 'rgba(232,189,118,0.14)' : '#fff',
                }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2, textTransform: 'capitalize' }}>
                    {monthName(m.month, null, 'short').replace('.', '')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.2 }}>
                    {m.year}
                  </Typography>
                  <Box sx={{ height: 16, mt: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {count ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : required ? (
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: BRAND.copperDark, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('bankUpload.requiredShort', 'Req.')}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        {undatedCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('bankUpload.undated', { count: undatedCount, defaultValue: '+ {{count}} earlier statement(s) on file without a month' })}
          </Typography>
        )}

        {/* Drop zone */}
        <Box
          onClick={() => !uploading && openPicker()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !uploading) { e.preventDefault(); openPicker(); } }}
          sx={{
            mt: 2,
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: 2,
            border: '2px dashed',
            borderColor: dragOver ? BRAND.copper : 'rgba(172,104,46,0.35)',
            bgcolor: dragOver ? 'rgba(232,189,118,0.18)' : 'rgba(232,189,118,0.06)',
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            transition: 'all 0.15s',
            '&:hover': { borderColor: BRAND.copper, bgcolor: 'rgba(232,189,118,0.12)' },
            '&:focus-visible': { outline: `2px solid ${BRAND.copper}`, outlineOffset: 2 },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: BRAND.copper, mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {t('bankUpload.dropHere', 'Drag & drop your statements here, or ')}
            </Box>
            <Box component="span" sx={{ color: BRAND.copperDark, textDecoration: 'underline' }}>
              {t('bankUpload.chooseFiles', 'choose files')}
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('bankUpload.hint', 'Select several files at once · PDF, JPG or PNG · we match each file to its month')}
          </Typography>
        </Box>

        {/* Review list */}
        {items.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.2, color: 'text.secondary' }}>
              {t('bankUpload.reviewTitle', 'Confirm the month for each statement')}
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {items.map((item) => {
                const fatal = item.status === 'error' && !item.attempted;
                const duplicate = item.monthKey && coverage[item.monthKey];
                return (
                  <Box key={item.id} sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: item.status === 'error' ? 'error.light' : BRAND.border,
                    bgcolor: item.status === 'error' ? 'rgba(211,47,47,0.04)' : '#fff',
                  }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        {item.status === 'error'
                          ? <ErrorOutlineIcon color="error" />
                          : <FileIcon sx={{ color: BRAND.copper }} />}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.file.name}
                          </Typography>
                          <Typography variant="caption" color={item.error ? 'error' : 'text.secondary'}>
                            {item.error || formatSize(item.file.size)}
                          </Typography>
                        </Box>
                      </Stack>
                      {!fatal && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          {item.detected && item.status === 'pending' && (
                            <Tooltip arrow title={t('bankUpload.detectedTip', 'Detected from the file name. Change it if needed.')}>
                              <AutoAwesomeIcon sx={{ fontSize: 18, color: BRAND.copper }} />
                            </Tooltip>
                          )}
                          <Select
                            size="small"
                            value={item.monthKey}
                            displayEmpty
                            disabled={uploading}
                            onChange={(e) => updateItem(item.id, { monthKey: e.target.value, detected: false })}
                            sx={{ minWidth: 180, bgcolor: '#fff', textTransform: 'capitalize' }}
                          >
                            <MenuItem value="" disabled>{t('bankUpload.pickMonth', 'Select month')}</MenuItem>
                            {selectable.map((m) => (
                              <MenuItem key={m.key} value={m.key} sx={{ textTransform: 'capitalize' }}>
                                {monthName(m.month, m.year)}
                              </MenuItem>
                            ))}
                          </Select>
                        </Stack>
                      )}
                      {item.status === 'uploading' ? (
                        <CircularProgress size={22} sx={{ mx: 1 }} />
                      ) : (
                        <IconButton size="small" onClick={() => removeItem(item.id)} disabled={uploading}
                          aria-label={t('bankUpload.remove', 'Remove')}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    {item.status === 'uploading' && (
                      <LinearProgress variant="determinate" value={item.progress} sx={{ mt: 1, height: 4 }} />
                    )}
                    {duplicate && item.status === 'pending' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: { sm: 4 } }}>
                        {t('bankUpload.alreadyHave', 'You already have a statement for this month. That is fine if it is a different account.')}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>

            {blocked && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {t('bankUpload.pickMonthsFirst', 'Select a month for each statement before uploading.')}
              </Alert>
            )}

            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={() => setItems([])} disabled={uploading} color="inherit">
                {t('bankUpload.clear', 'Clear')}
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={uploadAll}
                disabled={uploading || blocked || pendingCount === 0}
                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                sx={{ px: 4 }}
              >
                {uploading
                  ? t('bankUpload.uploading', 'Uploading…')
                  : t('bankUpload.uploadN', { count: pendingCount, defaultValue: 'Upload {{count}} statement(s)' })}
              </Button>
            </Stack>
          </Box>
        )}

        {requiredDone >= REQUIRED_MONTHS && items.length === 0 && (
          <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ mt: 2 }}>
            {monthsOnFile >= MAX_MONTHS
              ? t('bankUpload.allTwelve', 'All 12 months are on file. Thank you!')
              : t('bankUpload.addMore', 'You are all set on required statements. Adding more months can unlock larger amounts and better rates.')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
});

BankStatementUploader.displayName = 'BankStatementUploader';

export default BankStatementUploader;
